'use client'

/**
 * Shell data shared by every page: the ticker (plans/03 A5.4), nav badges
 * (A3) and the persona names (A3). One request loop per window each.
 */

import { useEffect } from 'react'
import { createPolledResource } from '@/lib/polled'
import { usePersona, setPersonaRoster } from '@/lib/persona'
import { onAutoFrame, onStoryStep } from '@/hooks/useAutoStream'
import { apiClient } from '@/lib/api-client'
import { onStoryMoved } from '@/lib/story'
import type { NavBadges, PersonasPayload, TickerPayload } from '@/lib/dr-types'

const TICKER_POLL_MS = 10_000
const BADGES_POLL_MS = 15_000

const tickerResource = createPolledResource<TickerPayload>('/api/dr/ticker', TICKER_POLL_MS)
const badgesResource = createPolledResource<NavBadges>('/api/dr/nav-badges', BADGES_POLL_MS)
let badgesPersona: string | null = null

/** Frames that change what the ticker or the badges show. */
function isShellRefetchFrame(kind: string): boolean {
  return kind.startsWith('dr.') || kind === 'workbench.raised'
}

/**
 * One set of frame and story listeners per window for a resource, however many components read it, so a burst of frames
 * (or several badge readers) makes one request instead of one per reader and frame.
 */
function windowRefetcher(refetchSoon: () => void, alsoOnStep: boolean) {
  let readers = 0
  let off: (() => void) | null = null
  return () => {
    readers += 1
    if (readers === 1) {
      const offFrame = onAutoFrame((frame) => {
        if (isShellRefetchFrame(frame.kind)) refetchSoon()
      })
      const offMoved = onStoryMoved(refetchSoon)
      const offStep = alsoOnStep ? onStoryStep(refetchSoon) : () => undefined
      off = () => {
        offFrame()
        offMoved()
        offStep()
      }
    }
    return () => {
      readers -= 1
      if (readers === 0 && off) {
        off()
        off = null
      }
    }
  }
}

const tickerListeners = windowRefetcher(() => tickerResource.refetchSoon(), false)
const badgeListeners = windowRefetcher(() => badgesResource.refetchSoon(), true)

/** The ticker payload; refetched on any `dr.*` or `workbench.raised` frame and when the story moves. */
export function useTicker(): TickerPayload | null {
  const snapshot = tickerResource.useResource()
  useEffect(() => tickerListeners(), [])
  return snapshot.data
}

/** Nav badges for the current persona; refetched on persona change, story steps and Workbench frames. */
export function useNavBadges(): NavBadges {
  const snapshot = badgesResource.useResource()
  const { persona } = usePersona()
  // Refetch once per persona change for the whole window (several components read the badges).
  useEffect(() => {
    if (badgesPersona !== null && badgesPersona !== persona) void badgesResource.refetch()
    badgesPersona = persona
  }, [persona])
  useEffect(() => badgeListeners(), [])
  return snapshot.data ?? {}
}

let rosterRequested = false

/** Loads persona names from `GET /api/dr/personas` once per window; the built-in names stand if it fails. */
export function usePersonaRoster(): void {
  useEffect(() => {
    if (rosterRequested) return
    rosterRequested = true
    apiClient
      .get<PersonasPayload>('/api/dr/personas')
      .then((payload) => setPersonaRoster(payload?.personas))
      .catch(() => {
        // The built-in roster in lib/persona.ts stays in use.
      })
  }, [])
}
